import { defineStore } from 'pinia'
import { ref } from 'vue'

import { dropdownValuesApi } from '../api/DropdownValuesApi'
import type {
  DropdownableDimension,
  ScheduleTabMap,
  WorkspaceSchedule,
} from '../models/schedule.types'

import type { BootstrapScheduleGroup } from '@/features/account'
import { ApiError } from '@/shared/http'

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function isDropdownableDimension(value: unknown): value is DropdownableDimension {
  if (!isRecord(value) || typeof value.unique_count !== 'number') return false
  return (
    value.values === undefined ||
    (Array.isArray(value.values) && value.values.every((item) => typeof item === 'string'))
  )
}

/** Module 08's bootstrap-backed schedule/dropdownable metadata store (contract 08 §7–§9). */
export const useReportScheduleStore = defineStore('reportSchedules', () => {
  const accountId = ref<number | null>(null)
  const schedulesByWorkspace = ref<Record<string, WorkspaceSchedule[]>>({})
  const dropdownablesBySchedule = ref<Record<string, unknown>>({})
  const dropdownValuesCache = ref<Record<string, string[]>>({})
  const inflightDropdownValues = new Map<string, Promise<string[]>>()
  let dropdownValuesGeneration = 0

  /** Clear prior-tenant metadata before accepting a new account's degraded bootstrap. */
  function selectAccount(nextAccountId: number): void {
    if (accountId.value !== null && accountId.value !== nextAccountId) reset()
    accountId.value = nextAccountId
  }

  function setSchedules(groups: BootstrapScheduleGroup[]): void {
    const next: Record<string, WorkspaceSchedule[]> = {}
    for (const group of groups) {
      next[String(group.workspace_id)] = group.schedules.map((schedule) => ({ ...schedule }))
    }
    schedulesByWorkspace.value = next
  }

  function setDropdownables(next: Record<string, unknown>): void {
    resetDropdownValueCache()
    dropdownablesBySchedule.value = { ...next }
  }

  function schedulesForWorkspace(workspaceId: number | string | undefined): WorkspaceSchedule[] {
    return workspaceId === undefined ? [] : (schedulesByWorkspace.value[String(workspaceId)] ?? [])
  }

  function getScheduleByReportId(
    reportId: number,
    workspaceIds: readonly (number | string)[],
  ): WorkspaceSchedule | null {
    for (const workspaceId of workspaceIds) {
      const schedule = schedulesForWorkspace(workspaceId).find(
        (item) => item.report_id === reportId,
      )
      if (schedule) return schedule
    }
    return null
  }

  function getScheduleTabs(schedule: WorkspaceSchedule | null): ScheduleTabMap {
    const tabs = schedule?.additional_info?.tabs
    return isRecord(tabs) ? (tabs as ScheduleTabMap) : {}
  }

  function getDropdownableDimension(
    scheduleId: number | string,
    field: string,
  ): DropdownableDimension | null {
    const scheduleDropdownables = dropdownablesBySchedule.value[String(scheduleId)]
    if (!isRecord(scheduleDropdownables)) return null
    const dimension = scheduleDropdownables[field]
    return isDropdownableDimension(dimension) ? dimension : null
  }

  async function loadDropdownValues(
    workspaceId: number | string,
    scheduleId: number | string,
    dimension: string,
  ): Promise<string[]> {
    const selectedAccountId = accountId.value
    if (selectedAccountId === null) return []

    const key = `${workspaceId}:${scheduleId}:${dimension}`
    const cached = dropdownValuesCache.value[key]
    if (cached !== undefined) return [...cached]

    // Backward compatibility for a full bootstrap: present `[]` is authoritative; absent means lazy.
    const bootValues = getDropdownableDimension(scheduleId, dimension)?.values
    if (bootValues !== undefined) {
      const values = [...bootValues]
      dropdownValuesCache.value[key] = values
      return [...values]
    }

    const inflight = inflightDropdownValues.get(key)
    if (inflight) return inflight.then((values) => [...values])

    const generation = dropdownValuesGeneration
    const cacheIfCurrent = (values: string[]): string[] => {
      const result = [...values]
      if (generation === dropdownValuesGeneration && accountId.value === selectedAccountId) {
        dropdownValuesCache.value[key] = result
        return result
      }
      return []
    }
    const request = dropdownValuesApi
      .fetchDropdownValues(selectedAccountId, workspaceId, scheduleId, dimension)
      .then((response) => cacheIfCurrent(response.data.values ?? []))
      .catch((error: unknown) => {
        if (ApiError.isApiError(error) && error.status === 404) return cacheIfCurrent([])
        throw error
      })
      .finally(() => {
        if (inflightDropdownValues.get(key) === request) inflightDropdownValues.delete(key)
      })
    inflightDropdownValues.set(key, request)
    return request.then((values) => [...values])
  }

  function resetSchedules(): void {
    schedulesByWorkspace.value = {}
    accountId.value = null
    resetDropdownValueCache()
  }

  function resetDropdownables(): void {
    dropdownablesBySchedule.value = {}
    resetDropdownValueCache()
  }

  /**
   * Per-navigation reset (05 `registerNavReset`, ORP-18644 AC6). Drops the shared in-flight
   * promises ONLY: a request whose consumer has navigated away must never be handed to the next
   * page, and after `cancelInFlightGuard` has fired, a still-mapped promise may already be a
   * rejected one. The generation bump is what stops a late settle re-registering into the freshly
   * cleared map — so it must INCREMENT, never reset to zero, or a stale request would match again.
   *
   * Settled values in `dropdownValuesCache` deliberately survive: v3 cleared report DATA
   * (`utils/reportCache.js`), which has no v4 counterpart, and dropping these instead would only
   * add refetches on every navigation.
   */
  function resetInflightDropdownValues(): void {
    dropdownValuesGeneration += 1
    inflightDropdownValues.clear()
  }

  function resetDropdownValueCache(): void {
    resetInflightDropdownValues()
    dropdownValuesCache.value = {}
  }

  function reset(): void {
    resetSchedules()
    resetDropdownables()
  }

  return {
    accountId,
    schedulesByWorkspace,
    dropdownablesBySchedule,
    selectAccount,
    setSchedules,
    setDropdownables,
    schedulesForWorkspace,
    getScheduleByReportId,
    getScheduleTabs,
    getDropdownableDimension,
    loadDropdownValues,
    resetInflightDropdownValues,
    resetSchedules,
    resetDropdownables,
    reset,
  }
})

export type ReportScheduleStore = ReturnType<typeof useReportScheduleStore>
